import { Shield, FileCheck, Clock, DollarSign, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const ProductionPage = () => {
  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="production-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6" data-testid="production-heading">
            Production Services
          </h1>
          <p className="text-lg text-[#8C8C8C] max-w-2xl mx-auto">
            Professional, compliant, and production-ready artistry for film, photo, and commercial projects.
          </p>
        </div>

        {/* Industry Standards */}
        <div className="bg-white p-8 md:p-12 rounded-lg shadow-sm mb-12">
          <h2 className="text-3xl font-bold mb-8">Industry Standards & Compliance</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-semibold mb-2">NDA Ready</h3>
                <p className="text-[#8C8C8C]">
                  Full confidentiality agreements available for sensitive projects and high-profile clients.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <FileCheck className="w-8 h-8 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-semibold mb-2">OSHA Compliant</h3>
                <p className="text-[#8C8C8C]">
                  Safety protocols and on-set standards fully adhered to for professional environments.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <FileCheck className="w-8 h-8 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-semibold mb-2">State Board Certified</h3>
                <p className="text-[#8C8C8C]">
                  All artists maintain current state licensure and certifications.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-semibold mb-2">Barbicide Certified</h3>
                <p className="text-[#8C8C8C]">
                  Sanitation and disinfection standards rigorously maintained.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Offered */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-8">Production Services Offered</h2>
          <div className="space-y-6">
            <div className="bg-[#F2EBE5] p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Film & Television</h3>
              <p className="text-[#8C8C8C] mb-4">
                On-set hair and makeup for narrative films, documentaries, commercials, and TV productions.
              </p>
              <ul className="space-y-2 text-[#8C8C8C] text-sm">
                <li>• Continuity management</li>
                <li>• Period-specific styling</li>
                <li>• Theatrical makeup</li>
                <li>• Multi-day production coordination</li>
              </ul>
            </div>

            <div className="bg-[#F2EBE5] p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Photo Shoots</h3>
              <p className="text-[#8C8C8C] mb-4">
                Editorial, fashion, commercial, and portrait photography beauty services.
              </p>
              <ul className="space-y-2 text-[#8C8C8C] text-sm">
                <li>• Multiple look changes</li>
                <li>• On-location or studio beauty services</li>
                <li>• Camera-ready finishes</li>
                <li>• Quick styling between setups</li>
              </ul>
            </div>

            <div className="bg-[#F2EBE5] p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Commercial & Corporate</h3>
              <p className="text-[#8C8C8C] mb-4">
                Brand campaigns, corporate videos, and promotional content.
              </p>
              <ul className="space-y-2 text-[#8C8C8C] text-sm">
                <li>• Talent preparation</li>
                <li>• Brand guideline adherence</li>
                <li>• Professional on-set etiquette</li>
                <li>• Rapid touch-ups between takes</li>
              </ul>
            </div>
          </div>
        </div>

        {/* On-Set Etiquette */}
        <div className="bg-white p-8 md:p-12 rounded-lg shadow-sm mb-12">
          <h2 className="text-3xl font-bold mb-6">On-Set Professionalism</h2>
          <p className="text-[#8C8C8C] mb-6 leading-relaxed">
            We understand production environments require discretion, efficiency, and adaptability. 
            Our artists arrive prepared, work seamlessly with your crew, and maintain focus on your creative vision.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-[#D4AF37]" />
              <span className="font-medium">Punctual & Reliable</span>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#D4AF37]" />
              <span className="font-medium">Discreet & Professional</span>
            </div>
            <div className="flex items-center gap-3">
              <FileCheck className="w-6 h-6 text-[#D4AF37]" />
              <span className="font-medium">Adaptable & Efficient</span>
            </div>
          </div>
        </div>

        {/* Hourly Touch-Ups */}
        <div className="bg-[#F2EBE5] p-8 md:p-12 rounded-lg mb-12">
          <h2 className="text-3xl font-bold mb-4">Hourly Touch-Up Availability</h2>
          <p className="text-[#8C8C8C] mb-6 leading-relaxed">
            Extended shoots require ongoing maintenance. We offer hourly touch-up services to keep talent camera-ready throughout your production day.
          </p>
          <p className="text-sm text-[#8C8C8C]">
            Minimum booking requirements apply. Rates vary based on project scope and duration.
          </p>
        </div>

        {/* Rate Card CTA */}
        <div className="bg-white p-8 md:p-12 rounded-lg shadow-sm border-2 border-[#D4AF37]">
          <div className="flex items-start gap-4 mb-6">
            <DollarSign className="w-10 h-10 text-[#D4AF37] flex-shrink-0" />
            <div>
              <h2 className="text-3xl font-bold mb-3">Request Industry Rate Card</h2>
              <p className="text-[#8C8C8C] leading-relaxed">
                Production rates are customized based on project type, duration, and specific requirements. 
                Submit an inquiry to receive our detailed rate card and availability.
              </p>
            </div>
          </div>
          <Link
            to="/inquiry"
            data-testid="production-cta-button"
            className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#b89530] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
          >
            Request Rate Card
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Additional Note */}
        <div className="mt-8 text-center">
          <p className="text-sm text-[#8C8C8C]">
            All production inquiries are handled with confidentiality. 
            Rate cards and availability are provided after initial consultation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductionPage;